import {
  AppointmentStatusCodes,
  AppointmentEventDto,
  ResourceDto,
  QueryFactory,
} from '@lmt/shared-types';

import { useOpenCalendarTab } from '@lmt/unicorn-shared/app/components/calendar/devextreme/components/useOpenCalendarTab';
import { ListType } from '@lmt/unicorn-shared/app/components/calendar/PatientAndWaitingListNavigation';
import { stopPropagation } from '@lmt/unicorn-shared/app/components/common/types';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  getFormattedDateTimeRange,
  getFormattedTimeRange,
  isMiniscreen,
  isStartMiniscreenOfflineModeEnabled,
  isStartMiniscreenOnlineModeEnabled,
  MiniscreenMode,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { VisitStatusOption } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/case-tree/VisitStatusOption';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import MailIcon from '@lmt/unicorn-shared/app/icons/mail.svg?react';
import CrossIcon from '@lmt/unicorn-shared/app/icons/med-cross.svg?react';
import ResourceIcon from '@lmt/unicorn-shared/app/icons/resource.svg?react';
import PatientIcon from '@lmt/unicorn-shared/app/icons/patient.svg?react';
import PhoneIcon from '@lmt/unicorn-shared/app/icons/phone.svg?react';
import CancelIcon from '@lmt/unicorn-shared/app/icons/red-cross.svg?react';
import RoomIcon from '@lmt/unicorn-shared/app/icons/room.svg?react';
import SaveIcon from '@lmt/unicorn-shared/app/icons/small-tick.svg?react';
import TagIcon from '@lmt/unicorn-shared/app/icons/tag.svg?react';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { emailRegexp } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import styles from './AppointmentPopup.module.scss';
import { AppointmentPatientInfo } from './types';
import { useTags } from '../../../common/ui-kit/tags/useTags';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { MiniscreenModeIcon } from '../../../common/icons/miniscreen/MiniscreenModeIcon';
import { useModal } from '../../../common/ui-kit/modal/useModal';
import { ConfirmOptions } from '../../../common/ui-kit/modal/useModal.types';

export type AppointmentPopupProps = {
  appointmentData?: AppointmentEventDto;
  patient?: AppointmentPatientInfo;
  doctor?: ResourceDto | null | undefined;
  room?: ResourceDto | null | undefined;
  resources?: ResourceDto[] | null | undefined;
  subtenantCode: string;
  priorityContact?: string;
  hideDate?: boolean;
  className?: string;
  setShownListType?: (value: ListType) => void;
};

export const AppointmentPopup: React.FC<
  AppointmentPopupProps & {
    onClose?: () => void; // action to execute when any popup button is clicked
  }
> = (props) => {
  const forms = useGlobalForms();
  const { patient, doctor, room, appointmentData, priorityContact, resources } =
    props;
  const tabs = useTabs();
  const modals = useModal();
  const i18n = useScopedTranslation('patients.overview.visits');
  const i18nextMS = useScopedTranslation('patients.list.options');
  const claims = useClaims();
  const tags = useTags();
  const tagsDict = useMemo(() => {
    const dict: { [key: number]: string } = {};
    tags.forEach((tag) => {
      dict[tag.id] = tag.value;
    });
    return dict;
  }, [tags]);

  if (!appointmentData || !patient) {
    return null;
  }
  const openCalendarTab = useOpenCalendarTab(modals, props);
  const changeVisitStatus = async (newStatus: AppointmentStatusCodes) => {
    let dataForConfirm: ConfirmOptions;
    let methodToUpdate: (id: number) => Promise<void>;
    switch (newStatus) {
      case AppointmentStatusCodes.Canceled:
        dataForConfirm = {
          title: i18n.t('confirm_cancel_visit_title'),
          text: i18n.t('confirm_cancel_visit_text'),
          okButtonText: i18n.t('confirm_cancel_visit_button'),
        };
        methodToUpdate = (id) =>
          QueryFactory.AppointmentEventsClient.cancelVisit(id);
        break;
      case AppointmentStatusCodes.Completed:
        dataForConfirm = {
          title: i18n.t('confirm_complete_visit_title'),
          text: i18n.t('confirm_complete_visit_text'),
          okButtonText: i18n.t('confirm_complete_visit_button'),
          okButtonColor: ButtonColor.Secondary,
        };
        methodToUpdate = (id) =>
          QueryFactory.AppointmentEventsClient.completeVisit(id);
        break;
      case AppointmentStatusCodes.Confirmed:
        dataForConfirm = {
          title: i18n.t('confirm_confirm_visit_title'),
          text: i18n.t('confirm_confirm_visit_text'),
          okButtonText: i18n.t('confirm_confirm_visit_button'),
          okButtonColor: ButtonColor.Secondary,
        };
        methodToUpdate = (id) =>
          QueryFactory.AppointmentEventsClient.confirmVisit(id);
        break;
      default:
        throw new Error('Invalid newStatus in changeVisitStatus');
    }
    const isConfirmed = await modals.showConfirm(dataForConfirm);
    if (isConfirmed) {
      try {
        await methodToUpdate(appointmentData.id);
        await openCalendarTab(appointmentData.start);
      } catch (err) {
        await modals.showError({ text: convertToErrorString(err) });
      }
    }
  };

  const PatientInfo = useCallback(() => {
    if (!patient) {
      return null;
    }
    return (
      <div>
        <div className={styles.info}>
          <PatientIcon className={styles.icon} />
          <UniLink
            color={ButtonColor.Primary}
            title={patient.fullName}
            onClick={() => {
              props.onClose?.();
              // Fix bug #32779: setTimeout is added to make sure that popup closing is executed before tab is opened
              setTimeout(() => {
                tabs.openTab(
                  pages.patientProfile.link({ patientId: patient.id }),
                );
              }, 10);
            }}
          />
          <div className={styles.code}>{props.subtenantCode}</div>
        </div>
        {priorityContact && (
          <div className={styles.info}>
            {priorityContact.match(emailRegexp) ? (
              <MailIcon className={styles.icon} />
            ) : (
              <PhoneIcon className={styles.icon} />
            )}
            {priorityContact}
          </div>
        )}
      </div>
    );
  }, []);

  const StatusControl = useMemo(() => {
    const dateNow = new Date();
    const showCancelButton =
      appointmentData.statusId === AppointmentStatusCodes.Planned ||
      appointmentData.statusId === AppointmentStatusCodes.Confirmed;

    const showConfirmButton =
      dateNow < appointmentData.start &&
      appointmentData.statusId === AppointmentStatusCodes.Planned;
    const showCompleteButton =
      dateNow >= appointmentData.start &&
      (appointmentData.statusId === AppointmentStatusCodes.Confirmed ||
        appointmentData.statusId === AppointmentStatusCodes.Planned);
    return (
      <div onClick={stopPropagation}>
        {showCancelButton && (
          <UniLink
            icon={<CancelIcon />}
            title={i18n.t('cancel_button')}
            color={ButtonColor.Danger}
            onClick={() => {
              props.onClose?.();
              changeVisitStatus(AppointmentStatusCodes.Canceled);
            }}
          />
        )}
        {showConfirmButton && (
          <UniLink
            icon={<SaveIcon />}
            title={i18n.t('confirm_button')}
            color={ButtonColor.Secondary}
            className={styles.link}
            onClick={() => {
              props.onClose?.();
              changeVisitStatus(AppointmentStatusCodes.Confirmed);
            }}
          />
        )}
        {showCompleteButton && (
          <UniLink
            icon={<SaveIcon />}
            title={i18n.t('complete_button')}
            color={ButtonColor.Secondary}
            className={styles.link}
            onClick={() => {
              props.onClose?.();
              changeVisitStatus(AppointmentStatusCodes.Completed);
            }}
          />
        )}
      </div>
    );
  }, [appointmentData, appointmentData.statusId, appointmentData.resourceIds]);

  return (
    <div
      className={clsx(styles.container, props.className)}
      onClick={(e) => {
        // we don't need to open Visit Editing if we clicked on 'Cancel'/'Complete' or Patient name in popup
        if ((e.target as any).localName === 'a') return;

        forms.openVisitEditing(appointmentData!.id, true);
        props.onClose?.();
      }}
    >
      {!props.hideDate && (
        <div className={styles.title}>
          {localFormat(appointmentData.start, 'MMMM d')}
        </div>
      )}
      <div className={styles.appointmentInfo}>
        <div className={styles.row}>
          <div className={styles.rowGroup}>
            {appointmentData.start.getDate() === appointmentData.end.getDate()
              ? getFormattedTimeRange(
                  appointmentData.start,
                  appointmentData.end,
                )
              : getFormattedDateTimeRange(
                  appointmentData.start,
                  appointmentData.end,
                )}

            {!(
              new Date() >= appointmentData.start &&
              (appointmentData.statusId === AppointmentStatusCodes.Planned ||
                appointmentData.statusId === AppointmentStatusCodes.Confirmed)
            ) && <VisitStatusOption status={appointmentData.statusId} />}
          </div>
          <div className={styles.controlPanel}>{StatusControl}</div>
        </div>
        <div
          data-status={
            appointmentData.statusId === AppointmentStatusCodes.Canceled
              ? 'canceled'
              : appointmentData.statusId === AppointmentStatusCodes.Completed
                ? 'completed'
                : undefined
          }
          className={styles.appointmentTitle}
        >
          <div className={styles.ellipse} />
          {appointmentData.title}
        </div>

        {!!room && (
          <div className={styles.info}>
            <RoomIcon className={styles.icon} />
            <div>{room.name}</div>
          </div>
        )}

        <div className={styles.row}>
          <PatientInfo />
          {isElectron() &&
            isMiniscreen() &&
            claims.permissions.importMeasurements && (
              <div className={styles.controls}>
                {isStartMiniscreenOnlineModeEnabled() && (
                  <IconButton
                    disabled={!isMiniscreen()}
                    className={styles.miniscreenIconButton}
                    iconComponent={
                      <MiniscreenModeIcon
                        type="online"
                        className={styles.miniscreenIcon}
                      />
                    }
                    onClick={() =>
                      openWithMiniscreen({
                        patientId: patient.id,
                        mode: MiniscreenMode.Online,
                      })
                    }
                    tooltip={i18nextMS.t('start_miniscreen_online')}
                  />
                )}
                {isStartMiniscreenOfflineModeEnabled() && (
                  <IconButton
                    disabled={!isMiniscreen()}
                    className={styles.miniscreenIconButton}
                    iconComponent={
                      <MiniscreenModeIcon
                        type="offline"
                        className={styles.miniscreenIcon}
                      />
                    }
                    onClick={() =>
                      openWithMiniscreen({
                        patientId: patient.id,
                        mode: MiniscreenMode.Offline,
                      })
                    }
                    tooltip={i18nextMS.t('start_miniscreen_offline')}
                  />
                )}
              </div>
            )}
        </div>

        {!!doctor && (
          <div className={styles.info}>
            <CrossIcon className={styles.icon} />
            <div>{doctor.name}</div>
          </div>
        )}

        {!!resources?.length && (
          <div className={styles.resourcesInfo}>
            <ResourceIcon className={styles.resourcesIcon} />
            <div className={styles.resources}>
              {resources?.map((resource) => resource.name).join(', ')}
            </div>
          </div>
        )}

        {!!appointmentData.tags.length && (
          <div className={styles.tagsInfo}>
            <TagIcon className={styles.icon} />
            <div className={styles.tagList}>
              {appointmentData.tags.map((tagId) => (
                <UniLink
                  key={tagId}
                  className={styles.tag}
                  title={`#${tagsDict[tagId]}`}
                  color={ButtonColor.Secondary}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
